import { APIModule } from '@modules/api';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { BaseModule, INJECTION_TOKEN, SYSTEMS_TOKEN } from '@modules/core';
import { AsyncTextureLoader, Entity, getDeviceInfo, StandardModule } from '@modules/standard';
import { DynamicParam } from '@zams/logger/zed/generated/loggerMapType';

import { TourDanjiCameraAPI } from './camera/TourDanjiCameraAPI';
import { CameraRotationSystem } from './ecs/system/CameraRotationSystem';
import { DanjiMeshSystem } from './ecs/system/DanjiMeshSystem';
import { DisableCollidedDongSystem } from './ecs/system/DisableCollidedDongSystem';
import { DongSystem } from './ecs/system/DongSystem';
import { FacilitySystem } from './ecs/system/FacilitySystem';
import { GroundSystem } from './ecs/system/GroundSystem';
import { GUIDisplaySystem } from './ecs/system/GUIDisplaySystem';
import { InteriorTourMovingSystem } from './ecs/system/InteriorTourMovingSystem';
import { ItemHoSystem } from './ecs/system/ItemHoSystem';
import { RaycastSystem } from './ecs/system/RaycastSystem';
import { ScreenDistanceSystem } from './ecs/system/ScreenDistanceSystem';
import { SkySystem } from './ecs/system/SkySystem';
import { SunSystem } from './ecs/system/SunSystem';
import { SupplementaryVisibleSystem } from './ecs/system/SupplementaryVisibleSystem';
import { TreeSystem } from './ecs/system/TreeSystem';
import { UnitModelSystem } from './ecs/system/UnitModelSystem';
import { UnitPanelSystem } from './ecs/system/UnitPanelSystem';
import { UnitPriceSystem } from './ecs/system/UnitPriceSystem';
import { UnitSystem } from './ecs/system/UnitSystem';
import { MaterialManager } from './graphic/material/MaterialManager';
import { LightManager } from './LightManager';
import {
  clearTourDanjiObservable,
  onCameraViewTypeChanged,
  onDongLoaded,
  onDongSelected,
  onFacilityLoaded,
  onGroundLoaded,
  onLoadCompleted,
  onPreLoad,
  onRoomTypeLoaded,
  onSeasonChanged,
  onTimeIsRunning,
  onUnitActivated,
  onUnitViewStateChanged,
  onUpdateCompassUI,
  onUpdateDongGUI,
  onUpdateFacilityGUI,
  onUpdateGisComplexGUI,
  onUpdateItemHoGUI,
  onUpdateRoomTypeFilter,
  onUpdateSunPositionGUI,
} from './observable/TourDanjiObservable';
import { TourDanjiFetcher } from './TourDanjiFetcher';
import { TourDanjiZed } from './TourDanjiZed';
export type TourDanjiZedModules = [BabylonjsModule, APIModule, StandardModule, TourDanjiModule];

export const TOURDANJI_MODULE_OPTIONS_TOKEN = Symbol.for('TOURDANJI_MODULE_OPTIONS_TOKEN');
export type TourDanjiOptions = {
  itemDongId?: string;
  itemHoId?: string;
  itemId?: number;
  disableFacility?: boolean;
  disableTree?: boolean;
  disableTransparent?: boolean;
  disableDecal?: boolean;
  renderingItemDongOnly?: boolean;
  domain?: 'zigbang' | 'hogangnono';
  zamParams?: Omit<DynamicParam['z131'], 'status_value'>;
  disableInteriorView?: boolean;
  areaHoId?: number;
};

export type TourDanjiMetaData = {
  danjiId: string;
  itemId: string;
  dong: string;
  ho: string;
  deviceTier: string;
  gpuModel: string;
  loadingTime: number;
  disableInteriorView: boolean;
} & Omit<DynamicParam['z131'], 'status_value'>;

let tourDanjiMetaData = {
  danjiId: '',
  itemId: '',
  dong: '',
  ho: '',
  deviceTier: '',
  gpuModel: '',
  loadingTime: 0,
  disableInteriorView: false,
} as TourDanjiMetaData;

export const setTourDanjiMetaData = (newData: TourDanjiMetaData) => {
  tourDanjiMetaData = newData;
};
export const getTourDanjiMetaData = () => tourDanjiMetaData;
export type DealType = '매매' | '전세' | '월세';
export type ScreenName = 'ZED매물상세' | 'ZED상세' | 'ZED매물카탈로그';
export const getTourDanjiZamsParams = () => {
  return {
    screen_name: tourDanjiMetaData.screen_name as ScreenName,
    area_danji_id: tourDanjiMetaData.danjiId,
    dong: tourDanjiMetaData.dong,
    item_id: tourDanjiMetaData.itemId,
    ...(tourDanjiMetaData.item_type && { item_type: tourDanjiMetaData.item_type }),
    ...(tourDanjiMetaData.ho_id && { ho_id: tourDanjiMetaData.ho_id }),
    ...(tourDanjiMetaData.deal_type && { deal_type: tourDanjiMetaData.deal_type as DealType }),
  };
};

export class TourDanjiModule implements BaseModule {
  static readonly INJECTABLE_TOKEN = Symbol.for('TourDanjiModule');
  static readonly [INJECTION_TOKEN] = [
    TourDanjiZed,
    TourDanjiCameraAPI,
    TourDanjiFetcher,
    LightManager,
    MaterialManager,
  ];
  static readonly [SYSTEMS_TOKEN] = [
    ScreenDistanceSystem,
    GroundSystem,
    SkySystem,
    SunSystem,
    DongSystem,
    FacilitySystem,
    GUIDisplaySystem,
    DanjiMeshSystem,
    RaycastSystem,
    TreeSystem,
    ItemHoSystem,
    CameraRotationSystem,
    DisableCollidedDongSystem,
    UnitSystem,
    UnitModelSystem,
    UnitPriceSystem,
    UnitPanelSystem,
    SupplementaryVisibleSystem,
    InteriorTourMovingSystem,
  ];

  readonly api;

  constructor({
    [TourDanjiZed.INJECTABLE_TOKEN]: zed,
    [AsyncTextureLoader.INJECTABLE_TOKEN]: asyncTextureLoader,
    [CameraRotationSystem.INJECTABLE_TOKEN]: cameraRotationSystem,
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: cameraAPI,
  }: {
    [TourDanjiZed.INJECTABLE_TOKEN]: TourDanjiZed;
    [AsyncTextureLoader.INJECTABLE_TOKEN]: AsyncTextureLoader;
    [CameraRotationSystem.INJECTABLE_TOKEN]: CameraRotationSystem;
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: TourDanjiCameraAPI;
  }) {
    // disable asyncTextureLoader
    asyncTextureLoader.setEnable(false);

    this.api = {
      _controller: zed,
      getEntityInfo: () => {
        const world = (zed as any).world;
        if (!world) return null;

        const entities = world.entities;
        const componentStats = new Map<string, number>();

        entities.forEach((entity: Entity) => {
          Object.keys(entity).forEach((key) => {
            if (key !== 'id') {
              componentStats.set(key, (componentStats.get(key) || 0) + 1);
            }
          });
        });

        return {
          totalEntities: entities.length,
          componentStats: Array.from(componentStats.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name, count]) => {
              const percentage = (count / entities.length) * 100;
              if (percentage < 20) return;

              return {
                name,
                count,
                percentage: percentage.toFixed(1),
              };
            })
            .filter((stat): stat is NonNullable<typeof stat> => stat !== undefined),
        };
      },
      loadDanji: zed.loadDanji.bind(zed),
      unloadDanji: zed.unloadDanji.bind(zed),
      focus: zed.focus.bind(zed),
      changeNextViewDirection: cameraRotationSystem.changeNextViewDirection.bind(cameraRotationSystem),
      getPerformanceInfo: zed.getProfileDebugInfo.bind(zed),
      updateRoomTypeResponse: zed.updateRoomTypeResponse.bind(zed),
      changeAreaUnit: zed.updateRoomTypeResponse.bind(zed),
      pushCameraView: zed.pushCameraView.bind(zed),
      popCameraView: zed.popCameraView.bind(zed),
      getCurrentDate: zed.getCurrentDate.bind(zed),
      setVisibleMeshes: zed.setVisibleMeshes.bind(zed),
      setTreeVisible: zed.setTreeVisible.bind(zed),
      getDefaultTreeVisibility: zed.getDefaultTreeVisibility.bind(zed),
      isTreeSystemLoaded: zed.isTreeSystemLoaded.bind(zed),
      changeUnitFloor: zed.changeUnitFloor.bind(zed),
      getCameraType: zed.getCameraType.bind(zed),
      enterUnit: zed.enterUnit.bind(zed),
      enterUnitById: zed.enterUnitById.bind(zed),
      exitUnit: zed.exitUnit.bind(zed),
      getUnitsInDong: zed.getUnitsInDong.bind(zed),
      selectDong: zed.selectDong.bind(zed),
      unselectDong: zed.unselectDong.bind(zed),
      focusOnDong: cameraAPI.focusOnDong.bind(cameraAPI),
      createUnitImmediately: zed.createUnitImmediately.bind(zed),
      observer: {
        onPreLoad: onPreLoad,
        onGroundLoaded: onGroundLoaded,
        onDongLoaded: onDongLoaded,
        onDongSelected: onDongSelected,
        onFacilityLoaded: onFacilityLoaded,
        onUpdateFacilityGUI: onUpdateFacilityGUI,
        onUpdateDongGUI: onUpdateDongGUI,
        onUpdateGisComplexGUI: onUpdateGisComplexGUI,
        onUpdateItemHoGUI: onUpdateItemHoGUI,
        onUpdateSunPositionGUI: onUpdateSunPositionGUI,
        onUpdateCompassUI: onUpdateCompassUI,
        onUpdateRoomTypeFilter: onUpdateRoomTypeFilter,
        onRoomTypeLoaded: onRoomTypeLoaded,
        onCameraViewTypeChanged: onCameraViewTypeChanged,
        onUnitActivated: onUnitActivated,
        onUnitViewStateChanged: onUnitViewStateChanged,
        onSeasonChanged: onSeasonChanged,
        onTimeIsRunning: onTimeIsRunning,
        onLoadCompleted: onLoadCompleted,
      },
    };
  }

  dispose() {
    clearTourDanjiObservable();
  }
}
